// This component doesn't render anything - it just manipulates the existing button

import { useCallback, useEffect, useRef, useState } from 'react'

type OtpResendCountdownProps = {
  countdown: string
  countdownText: string
  defaultCountdown: number
  resendText: string
}

export const OtpResendCountdown = ({
  countdown = '0',
  countdownText,
  resendText,
  defaultCountdown,
}: OtpResendCountdownProps) => {
  const initialCountdown = parseInt(countdown) || 0 // in seconds
  const [remainingSeconds, setRemainingSeconds] = useState<number>(initialCountdown)
  const remainingSecondsRef = useRef<number>(remainingSeconds)
  remainingSecondsRef.current = remainingSeconds
  const buttonRef = useRef<HTMLButtonElement | null>(null)
  const [intervalId, setIntervalId] = useState<NodeJS.Timeout | null>(null)
  const countdownInProgress = intervalId !== null
  const countdownInProgressRef = useRef<boolean>(countdownInProgress)
  countdownInProgressRef.current = countdownInProgress

  const decrementRemainingSeconds = useCallback(() => {
    setRemainingSeconds((seconds) => {
      const x = seconds ? seconds - 1 : 0
      remainingSecondsRef.current = x
      return x
    })
  }, [])

  const resetRemainingSeconds = useCallback(() => {
    setRemainingSeconds(defaultCountdown)
    remainingSecondsRef.current = defaultCountdown
  }, [defaultCountdown])

  const clear = useCallback(() => {
    setIntervalId((id) => {
      clearInterval(id ?? undefined)
      return null
    })
  }, [])

  const startCountdown = useCallback(() => {
    console.log('starting countdown')
    const interval = setInterval(() => {
      decrementRemainingSeconds()
      if (!remainingSecondsRef.current) {
        clear()
      }
    }, 1000)
    resetRemainingSeconds()

    setIntervalId((previous) => {
      clearInterval(previous ?? undefined)
      return interval
    })
  }, [clear, decrementRemainingSeconds, resetRemainingSeconds])

  const handleButtonClick = useCallback(
    (event: MouseEvent) => {
      if (countdownInProgressRef.current) return event.preventDefault()
      setTimeout(() => startCountdown(), 10)
    },
    [startCountdown]
  )

  useEffect(() => {
    const button = buttonRef.current ?? (document.getElementById('resend') as HTMLButtonElement)
    if (!button) return
    buttonRef.current = button
    button.addEventListener('click', handleButtonClick)
    startCountdown()
    return () => {
      button.removeEventListener('click', handleButtonClick)
      buttonRef.current = null
    }
  }, [handleButtonClick, startCountdown])

  useEffect(() => {
    if (!buttonRef.current) return
    if (remainingSeconds) {
      buttonRef.current.disabled = true
      buttonRef.current.textContent = countdownText.replace('%{seconds}', remainingSeconds.toString())
      return
    }
    buttonRef.current.disabled = false
    buttonRef.current.textContent = resendText
  }, [remainingSeconds, countdownText, resendText])

  return null
}
